import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard,
  Users,
  FileText,
  TrendingUp,
  ArrowRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Stats {
  total_users: number;
  total_organizations: number;
  total_conversations: number;
  total_messages: number;
  stripe_configured: boolean;
  paypal_configured: boolean;
}

interface PaymentStatus {
  stripe: { configured: boolean; mode: string };
  paypal: { configured: boolean; mode: string };
}

export default function AdminDashboard() {
  const { adminToken } = useAdminStore();
  const [stats, setStats] = useState<Stats | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        }),
        fetch(`${API_URL}/admin/payments`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        }),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json() as { success: boolean; data: Stats };
        if (statsData.success) {
          setStats(statsData.data);
        }
      }

      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json() as { success: boolean; data: PaymentStatus };
        if (paymentsData.success) {
          setPaymentStatus(paymentsData.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
        <p className="text-slate-500">
          Manage your SweetPilot platform settings
        </p>
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3 sm:gap-4">
        {[
          { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'purple' },
          { label: 'Organizations', value: stats?.total_organizations || 0, icon: FileText, color: 'cyan' },
          { label: 'Conversations', value: stats?.total_conversations || 0, icon: TrendingUp, color: 'green' },
          { label: 'Messages', value: stats?.total_messages || 0, icon: FileText, color: 'orange' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center flex-shrink-0`}>
                <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 text-${stat.color}-600`} />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-slate-900">{stat.value.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-slate-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Payment System Status */}
      <motion.div variants={fadeInUp}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900">Payment System Status</h2>
          <Link
            to="/admin/payments"
            className="flex items-center space-x-1 text-purple-600 hover:text-purple-700 transition-colors text-xs sm:text-sm"
          >
            <span>Configure</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Stripe Status */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-[#635BFF]" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Stripe</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">
                    {paymentStatus?.stripe.configured ? `Mode: ${paymentStatus.stripe.mode}` : 'Not configured'}
                  </p>
                </div>
              </div>
              <div className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0 ${
                paymentStatus?.stripe.configured
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}>
                {paymentStatus?.stripe.configured ? (
                  <>
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">Active</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">Inactive</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* PayPal Status */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#003087]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#003087] font-bold text-xs sm:text-sm">PP</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">PayPal</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">
                    {paymentStatus?.paypal.configured ? `Mode: ${paymentStatus.paypal.mode}` : 'Not configured'}
                  </p>
                </div>
              </div>
              <div className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0 ${
                paymentStatus?.paypal.configured
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}>
                {paymentStatus?.paypal.configured ? (
                  <>
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">Active</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm font-medium">Inactive</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={fadeInUp}>
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Link
            to="/admin/payments"
            className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm hover:border-purple-300 hover:shadow-md transition-all group"
          >
            <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mb-2 sm:mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-purple-600 transition-colors text-sm sm:text-base">
              Configure Payments
            </h3>
            <p className="text-xs sm:text-sm text-slate-500">
              Set up Stripe and PayPal payment gateways
            </p>
          </Link>
          <Link
            to="/admin/settings"
            className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm hover:border-cyan-300 hover:shadow-md transition-all group"
          >
            <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-600 mb-2 sm:mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-cyan-600 transition-colors text-sm sm:text-base">
              Site Settings
            </h3>
            <p className="text-xs sm:text-sm text-slate-500">
              Configure general platform settings
            </p>
          </Link>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm hover:border-green-300 hover:shadow-md transition-all group"
          >
            <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mb-2 sm:mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-green-600 transition-colors text-sm sm:text-base">
              View Website
            </h3>
            <p className="text-xs sm:text-sm text-slate-500">
              Open SweetPilot in a new tab
            </p>
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}
